import {
  Box,
  FormControl,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useState } from "react";

import { ExportIcon } from "assets/icons";
import { JANZU_URL } from "config/config";
import { colsVisibilityDefaultState } from "hooks/useColumnVisibility";
import { Modal } from "new-components/modals/Modal";
import { Typography } from "new-components/typography/Typography";
import { removeEmptyValuesFromObject } from "utils/redirectDataPage";
import { downloadFile, getFilenameFromResponse } from "utils/utilityFunctions";

const NON_EXPORTABLE_COLUMNS = ["is_audited"];

interface ImageModalProps {
  open: boolean;
  onClose: () => void;
  columnVisibilityModel: Record<string, boolean>;
  filtersObject: Record<string, string>;
}

export function ExportDataModal(props: ImageModalProps) {
  const { open, onClose, columnVisibilityModel, filtersObject } = props;

  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(false);
  const [valueFilter, setValueFilter] = useState<string>("active");
  const [valueFormat, setValueFormat] = useState<string>("csv");

  const handleChangeValueFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValueFilter(event.target.value);
  };

  const handleChangeValueFormat = (event: SelectChangeEvent) => {
    setValueFormat(event.target.value);
  };

  const filterAndJoinTrueKeys = (columns: Record<string, boolean>): string =>
    Object.entries(columns)
      .filter(([key, value]) => value && !NON_EXPORTABLE_COLUMNS.includes(key)) // Filter out entries with false values and non-exportable columns
      .map(([key]) => key) // Extract the keys
      .join(","); // Join the keys into a comma-separated string;

  const getExportableColumns = () =>
    valueFilter === "all"
      ? filterAndJoinTrueKeys(colsVisibilityDefaultState)
      : filterAndJoinTrueKeys(columnVisibilityModel) || filterAndJoinTrueKeys(colsVisibilityDefaultState);

  const createExportParams = () => ({
    detector_id: filtersObject?.detector_id,
    export_format: valueFormat,
    has_to_include_header: "true",
    exportable_columns: getExportableColumns(),
    filter_columns: valueFilter === "all" ? undefined : filtersObject?.filter_columns,
    filter_values: valueFilter === "all" ? undefined : filtersObject?.filter_values,
    filter_operators: valueFilter === "all" ? undefined : filtersObject?.filter_operators,
  });

  const handleDownloadResponse = async (response: Response) => {
    if (response.ok) {
      const filename = getFilenameFromResponse(response, `${filtersObject.detector_id}.${valueFormat}`);
      const blob = await response.blob();
      downloadFile(blob, filename);
      setLoading(false);
      enqueueSnackbar("Your file has been downloaded successfully", { variant: "success" });
      onClose();
      return;
    }
    setLoading(false);
    enqueueSnackbar("Error downloading file", { variant: "error" });
  };

  const handleClickExport = async () => {
    setLoading(true);
    const exportParams = createExportParams();
    const queryString = new URLSearchParams(removeEmptyValuesFromObject(exportParams)).toString();

    try {
      const response = await fetch(`${JANZU_URL}reef-api/image-queries/export-data-page?${queryString}`);
      await handleDownloadResponse(response);
    } catch {
      setLoading(false);
      enqueueSnackbar("Error downloading file", { variant: "error" });
    }
  };

  return (
    <Modal
      size="small"
      open={open}
      onClose={onClose}
      title="Export data"
      icon={<ExportIcon color="#ffffff" />}
      onCancel={onClose}
      confirmationButtonTitle={loading ? "Loading..." : "Export"}
      isConfirmationDisabled={loading}
      onConfirmation={handleClickExport}
      body={[
        <Box width="100%">
          <Box className="my-6 flex flex-col sm:flex-row justify-between gap-4 sm:gap-10">
            <FormControl className="flex flex-row items-center justify-start gap-2 w-full sm:w-[50%]">
              <FormLabel htmlFor="only-filters-radio" className="cursor-pointer">
                <Typography textTransform="uppercase" fontWeight="semibold">
                  Export Active Columns and Filters Only
                </Typography>
              </FormLabel>

              <Radio
                id="only-filters-radio"
                checked={valueFilter === "active"}
                onChange={handleChangeValueFilter}
                value="active"
                name="radio-buttons"
                inputProps={{ "aria-label": "Active" }}
              />
            </FormControl>

            <FormControl className="flex flex-row items-center justify-end gap-2 w-full sm:w-[50%]">
              <FormLabel htmlFor="all-data-radio" className="cursor-pointer">
                <Typography textTransform="uppercase" fontWeight="semibold">
                  Export all data
                </Typography>
              </FormLabel>
              <Radio
                id="all-data-radio"
                checked={valueFilter === "all"}
                onChange={handleChangeValueFilter}
                value="all"
                name="radio-buttons"
                inputProps={{ "aria-label": "All" }}
              />
            </FormControl>
          </Box>

          <Box className="mb-4 flex justify-center">
            <FormControl size="small">
              <InputLabel id="demo-simple-select-label">Select format type</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={valueFormat}
                onChange={handleChangeValueFormat}
                label="Select format type"
                className="w-[200px] bg-white"
                size="small"
              >
                <MenuItem value="csv">CSV</MenuItem>
                <MenuItem value="xlsx">XLSX</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>,
      ]}
    />
  );
}
